import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { api } from "../services/mmcapi";

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${d.getFullYear()}`;
};


const PaymentChargeTypes = () => {
  const role = useMemo(
    () => String(localStorage.getItem("role") || "").toUpperCase().replace(/[^A-Z]/g, ""),
    []
  );
  const isSuperAdmin = role === "SUPERADMIN"; 

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("active");
  const [total, setTotal] = useState(0);

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchList = useCallback(async () => {
   if (!isSuperAdmin) return; 
    try {
      const data = await api("paymentChargeType.getAll", {
        status,
        search: search || undefined,
        page,
        limit
      });
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(Number(data.pagination?.total) || 0);
    } catch (e) {
      toast.error(e.message);
      setItems([]);
      setTotal(0);
    }
  }, [isSuperAdmin, status, search, page, limit]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const currentPage = Math.min(Math.max(1, page), totalPages);

  const pageButtons = useMemo(() => {
    const windowSize = 5;
    const half = Math.floor(windowSize / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    const pages = [];
    for (let p = start; p <= end; p += 1) pages.push(p);
    return pages;
  }, [currentPage, totalPages]);

  const openCreate = () => {
    setName("");
    setShowCreate(true);
  };

  const closeCreate = () => {
    if (creating) return;
    setShowCreate(false);
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    const value = name.trim();
    if (!value) {
      toast.error("Enter a name");
      return;
    }
    setCreating(true);
    try {
      const data = await api("paymentChargeType.create", { name: value });
      toast.success(data?.message || "Payment charge type created");
      setShowCreate(false);
      setName("");
      setPage(1);
      setStatus("active");
      fetchList();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  const closeDelete = () => {
    if (deleting) return;
    setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      const data = await api("paymentChargeType.delete", { chargeTypeId: deleteTarget.id });
      toast.success(data?.message || "Payment charge type deleted successfully");
      setDeleteTarget(null);
      fetchList();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeleting(false);
    }
  };

 if (!isSuperAdmin) {   
    return (
      <div className="employee-form">
        <div className="content">
          <div className="container-fluid">
            <div className="card">
              <div className="card-body">You do not have permission to view this page.</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="employee-form">
      <div className="content-header">
        <div
          className="container-fluid d-flex justify-content-between align-items-end flex-wrap"
          style={{ gap: 12 }}
        >
          <div>
            <h1 className="m-0 txt_lft">Payment Charge Types</h1>
            <p style={{ margin: 0, color: "#6c757d", fontSize: 14 }}>
              Create and manage the charge types for your apartment.
            </p>
          </div>
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            Add Charge Type
          </button>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="card">
            <div className="card-body">
              <div className="role-table-box">
                <div
                  className="role-table-top d-flex flex-wrap align-items-end justify-content-start"
                  style={{ gap: 12 }}
                >
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      setPage(1);
                      setSearch(searchInput.trim());
                    }}
                    className="role-table-search-wrap"
                    style={{ flex: "1 1 220px", maxWidth: 360 }}
                  >
                    <div className="form-group mb-0">
                      <label className="form-label" style={{ fontSize: 12 }}>
                        Search
                      </label>
                      <input
                        type="text"
                        className="form-control role-table-search"
                        placeholder="Search charge type…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                      />
                    </div>
                  </form>
                </div>

                <div className="table-responsive">
                  <table className="table table-hover role-table">
                    <thead>
                      <tr>
                        <th><span className="role-th">Sl No.</span></th>
                        <th><span className="role-th">Name</span></th>
                        <th ><span className="role-th">Created On</span></th>
                        <th ><span className="role-th">Action</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length ? (
                        items.map((row, idx) => (
                          <tr key={String(row.id || `row-${idx}`)}>
                            <td>{(currentPage - 1) * limit + idx + 1}</td>
                            <td>{row.name}</td>
                            <td>{formatDate(row.createdAt)}</td>
                            <td>
                              {row.status === "active" ? (
                                <button
                                  type="button"
                                  className="role-action-btn role-action-delete"
                                  title="Delete"
                                  onClick={() => setDeleteTarget(row)}
                                >
                                  <i className="fa-solid fa-trash" aria-hidden="true" />
                                </button>
                              ) : (
                                "—"
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="text-center text-muted">
                            {search ? "No charge types found" : "No charge types"}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div
                  className="role-table-footer d-flex justify-content-between align-items-center flex-wrap"
                  style={{ gap: 10 }}
                >
                  <div className="d-flex align-items-center" style={{ gap: 10 }}>
                    <span className="text-muted" style={{ fontSize: 12 }}>Show:</span>
                    <select
                      className="form-control"
                      style={{ width: 90 }}
                      value={limit}
                      onChange={(e) => {
                        setPage(1);
                        setLimit(Number(e.target.value));
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="text-muted" style={{ fontSize: 12 }}>
                      Total: {total}
                    </span>
                  </div>

                  <div className="btn-group">
                    <button type="button" className="btn btn-light" disabled={currentPage <= 1} onClick={() => setPage(1)}>
                      &laquo;
                    </button>
                    <button type="button" className="btn btn-light" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                      &lsaquo;
                    </button>
                    {pageButtons.map((p) => (
                      <button
                        key={p}
                        type="button"
                        className={`btn ${p === currentPage ? "btn-primary" : "btn-light"}`}
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </button>
                    ))}
                    <button type="button" className="btn btn-light" disabled={currentPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                      &rsaquo;
                    </button>
                    <button type="button" className="btn btn-light" disabled={currentPage >= totalPages} onClick={() => setPage(totalPages)}>
                      &raquo;
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showCreate ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16
          }}
          onClick={closeCreate}
        >
          <div
            className="card app-modal-card"
            style={{ width: "min(440px, 96vw)", maxHeight: "90vh", overflow: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-header app-modal-header">
              <div className="app-modal-title">Add Charge Type</div>
              <button
                type="button"
                className="app-modal-close"
                onClick={closeCreate}
                disabled={creating}
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <form onSubmit={submitCreate}>
              <div className="card-body">
                <div className="form-group mb-0">
                  <label className="form-label">
                    Name <span className="required-star">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Water Charges"
                    value={name}
                    maxLength={100}
                    onChange={(e) => setName(e.target.value)}
                    autoFocus
                    required
                  />
                </div>
              </div>

              <div className="card-footer d-flex justify-content-end" style={{ gap: 10 }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeCreate}
                  disabled={creating}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={creating}>
                  {creating ? "Saving..." : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {deleteTarget ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16
          }}
          onClick={closeDelete}
        >
          <div
            className="card app-modal-card"
            style={{ width: "min(440px, 96vw)", maxHeight: "90vh", overflow: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-header app-modal-header">
              <div className="app-modal-title">Delete charge type</div>
              <button
                type="button"
                className="app-modal-close"
                onClick={closeDelete}
                disabled={deleting}
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <div className="card-body">
              Are you sure you want to delete <strong>{deleteTarget.name}</strong>?
            </div>

            <div className="card-footer d-flex justify-content-end" style={{ gap: 10 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeDelete}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PaymentChargeTypes;